import { createStore } from 'react-sweet-state';
import { produce } from 'immer';
import { cloneDeep } from 'lodash';

import { BRAND_VOICE_ASSET_FIELD } from 'src/data/brandVoice';
import { isSameLanguage } from 'src/data/generation';
import {
  createBrandVoice,
  deleteBrandVoice,
  queryBrandVoice,
  queryBrandVoicesAssets,
  queryBrandVoiceWithAssets,
  queryWorkspaceAssociatedBrandVoices,
  updateBrandVoice,
} from 'src/graphql/brandVoices';
import { queryCustomerBrandVoices } from 'src/graphql/customer';
import { setCustomFormatGroupDefaults } from 'src/graphql/format';
import { mergeEntityIntoEntityArray } from 'src/lib/collection';
import { isAbortError } from 'src/lib/errors';
import { captureMessage } from 'src/lib/sentry';

const INITIAL_STATE = {
  brandVoices: [],
  loadingBrandVoices: false,
  isSavingBrandVoice: false,
  deletingBrandVoice: false,

  customerBrandVoices: [],
  loadingCustomerBrandVoices: false,

  brandVoicesAssets: [],
  loadingBrandVoiceAssets: false,
  brandVoiceAssetsLoaded: false,

  defaultBrandVoice: null,
};

const PRIVATE_ACTIONS = {
  updateFormatGroupActivity:
    ({ brandVoiceId, formatGroupId, on }) =>
    ({ getState, setState }) => {
      const { brandVoicesAssets } = getState();

      const updatedAssets = produce(brandVoicesAssets, (draft) => {
        const brandVoice = draft.find((bv) => bv.id === brandVoiceId);
        if (!brandVoice) {
          return;
        }

        const formatGroup = brandVoice[BRAND_VOICE_ASSET_FIELD.customFormatGroups]?.find(
          (cfg) => cfg.group.id === formatGroupId
        );
        if (!formatGroup) {
          return;
        }

        formatGroup.on = on;
      });

      setState({ brandVoicesAssets: updatedAssets });
    },

  mergeBrandVoiceInCache:
    (brandVoice) =>
    ({ getState, setState }) => {
      const { brandVoices: workspaceList, customerBrandVoices: customerList } = getState();
      const newWorkspaceList = mergeEntityIntoEntityArray(workspaceList, brandVoice, {
        leading: true,
      });
      const newCustomerList = mergeEntityIntoEntityArray(customerList, brandVoice, {
        leading: true,
      });
      setState({ brandVoices: newWorkspaceList, customerBrandVoices: newCustomerList });
    },

  deleteBrandVoiceFromCache:
    (brandVoiceId) =>
    ({ getState, setState }) => {
      const {
        brandVoices: workspaceList,
        customerBrandVoices: customerList,
        brandVoicesAssets,
      } = getState();
      const newWorkspaceList = workspaceList.filter((brandVoice) => brandVoice.id !== brandVoiceId);
      const newCustomerList = customerList.filter((brandVoice) => brandVoice.id !== brandVoiceId);
      const newAssetsList = brandVoicesAssets.filter(
        (brandVoice) => brandVoice.id !== brandVoiceId
      );
      setState({
        brandVoices: newWorkspaceList,
        customerBrandVoices: newCustomerList,
        brandVoicesAssets: newAssetsList,
      });
    },

  mergeBrandVoiceAssetsInCache:
    (itemWithAssets) =>
    ({ getState, setState }) => {
      const { brandVoicesAssets } = getState();
      const newList = mergeEntityIntoEntityArray(brandVoicesAssets, itemWithAssets);
      setState({ brandVoicesAssets: newList });
    },
};

export const ACTIONS = {
  clearStore:
    () =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },

  queryCustomerBrandVoices:
    () =>
    async ({ setState }, { notifications, currentCustomerId }) => {
      try {
        setState({ loadingCustomerBrandVoices: true });
        const customer = await queryCustomerBrandVoices(currentCustomerId);

        setState({
          customerBrandVoices: customer.brandVoices,
          loadingCustomerBrandVoices: false,
        });
      } catch (error) {
        notifications.displayError(error);
      }
    },

  queryWorkspaceBrandVoices:
    ({ workspaceId }, options) =>
    async ({ setState }, { notifications, currentCustomerId }) => {
      try {
        setState({ loadingBrandVoices: true });
        const result = await queryWorkspaceAssociatedBrandVoices(
          {
            customerId: currentCustomerId,
            id: workspaceId,
          },
          options
        );

        setState({
          brandVoices: result?.associatedBrandVoices || [],
          defaultBrandVoice: result?.defaultBrandVoice || result?.associatedBrandVoices?.[0],
          loadingBrandVoices: false,
        });
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        notifications.displayError(error);
      }
    },

  createBrandVoice:
    ({ name, workspaceId, language }) =>
    async ({ setState, dispatch }, { currentCustomerId }) => {
      setState({ isSavingBrandVoice: true });
      try {
        const createdBrandVoice = await createBrandVoice({
          name,
          customerId: currentCustomerId,
          workspaceId,
          language,
        });
        dispatch(PRIVATE_ACTIONS.mergeBrandVoiceInCache(createdBrandVoice));
        dispatch(PRIVATE_ACTIONS.mergeBrandVoiceAssetsInCache(createdBrandVoice));
        return createdBrandVoice;
      } finally {
        setState({ isSavingBrandVoice: false });
      }
    },

  updateBrandVoice:
    ({ id, name, language }) =>
    async ({ setState, dispatch }) => {
      setState({ isSavingBrandVoice: true });
      try {
        const updatedBrandVoice = await updateBrandVoice({
          id,
          name,
          language,
        });
        dispatch(PRIVATE_ACTIONS.mergeBrandVoiceInCache(updatedBrandVoice));
      } finally {
        setState({ isSavingBrandVoice: false });
      }
    },

  queryBrandVoice:
    ({ id }) =>
    async ({ dispatch }) => {
      try {
        const brandVoice = await queryBrandVoice(id);
        dispatch(PRIVATE_ACTIONS.mergeBrandVoiceInCache(brandVoice));
      } catch (error) {
        captureMessage('Error while getting brand voice', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'BrandVoicesStore -> queryBrandVoice',
          },
        });
      }
    },

  deleteBrandVoice:
    (itemId) =>
    async ({ setState, dispatch }) => {
      setState({ deletingBrandVoice: true });
      try {
        await deleteBrandVoice(itemId);
        dispatch(PRIVATE_ACTIONS.deleteBrandVoiceFromCache(itemId));
      } finally {
        setState({ deletingBrandVoice: false });
      }
    },

  isSavingBrandVoice:
    () =>
    ({ getState }) => {
      return getState().isSavingBrandVoice;
    },

  getBrandVoices:
    () =>
    ({ getState }) => {
      return getState().brandVoices;
    },

  getDefaultBrandVoice:
    () =>
    ({ getState }) => {
      return getState().defaultBrandVoice;
    },

  queryBrandVoicesAssets:
    ({ customerId, workspaceId } = {}, options) =>
    async ({ setState }, { currentCustomerId, currentWorkspaceId }) => {
      // Support both `customerId` and `workspaceId` as parameters or values from the store container
      const localCustomerId = customerId || currentCustomerId;
      const localWorkspaceId = workspaceId || currentWorkspaceId;
      if (!localCustomerId || !localWorkspaceId) {
        return;
      }

      setState({ loadingBrandVoiceAssets: true });
      try {
        const brandVoicesAssets = await queryBrandVoicesAssets(
          {
            customerId: localCustomerId,
            workspaceId: localWorkspaceId,
          },
          options
        );

        setState({
          brandVoicesAssets: prepareBrandVoicesAssets(brandVoicesAssets),
          brandVoiceAssetsLoaded: true,
        });
      } catch (error) {
        captureMessage('Error while getting brand voices assets', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'BrandVoicesStore -> queryBrandVoicesAssets',
          },
        });
      } finally {
        setState({ loadingBrandVoiceAssets: false });
      }
    },

  getBrandVoicesAssets:
    ({ language } = {}) =>
    ({ getState }) => {
      if (!language) {
        return getState().brandVoicesAssets;
      }

      return getState().brandVoicesAssets.filter((voice) =>
        isSameLanguage(voice.language, language)
      );
    },

  getDefaultVoiceAssets:
    ({ language } = {}) =>
    ({ getState, dispatch }) => {
      const { defaultBrandVoice } = getState();

      const voicesAssets = dispatch(ACTIONS.getBrandVoicesAssets({ language }));
      return voicesAssets.find((voice) => voice.id === defaultBrandVoice?.id);
    },

  getBrandVoicesBrandTerms:
    (brandVoiceIds) =>
    ({ getState }) => {
      const { brandVoicesAssets } = getState();
      return brandVoicesAssets
        .filter((bv) => brandVoiceIds.includes(bv.id))
        .flatMap((bv) => bv.brandTerms);
    },

  storeBrandVoiceItemInCache:
    ({ brandVoiceId, itemsField, item }) =>
    ({ getState, setState }) => {
      const alignedBrandVoiceId = brandVoiceId || item.brandVoice?.id;
      if (!alignedBrandVoiceId) {
        return;
      }

      const { brandVoicesAssets } = getState();

      const updatedAssets = produce(brandVoicesAssets, (draft) => {
        // find or create the "brand voice" entry
        let brandVoice = draft.find((bv) => bv.id === alignedBrandVoiceId);
        if (!brandVoice) {
          brandVoice = { id: alignedBrandVoiceId };
          draft.push(brandVoice);
        }
        if (!brandVoice[itemsField]) {
          brandVoice[itemsField] = [];
        }

        // update or add the item in the specified entry type
        const existingItems = brandVoice[itemsField];
        const itemIndex = existingItems.findIndex((existingItem) => existingItem.id === item.id);
        if (itemIndex >= 0) {
          existingItems[itemIndex] = item;
        } else {
          existingItems.unshift(item);
        }
      });

      setState({ brandVoicesAssets: updatedAssets });
    },

  removeBrandVoiceItemFromCache:
    ({ brandVoiceId, itemsField, item }) =>
    ({ getState, setState }) => {
      const alignedBrandVoiceId = brandVoiceId || item.brandVoice?.id;
      if (!alignedBrandVoiceId) {
        return;
      }

      const { brandVoicesAssets } = getState();

      const updatedAssets = produce(brandVoicesAssets, (draft) => {
        const brandVoice = draft.find((bv) => bv.id === alignedBrandVoiceId);

        if (brandVoice) {
          // Safely filter out the item from the specified asset type
          brandVoice[itemsField] = brandVoice[itemsField]?.filter(
            (existingItem) => existingItem.id !== item.id
          );
        }
      });

      setState({ brandVoicesAssets: updatedAssets });
    },

  queryBrandVoiceWithAssets:
    (id) =>
    async ({ dispatch }) => {
      try {
        const brandVoiceWithAssets = await queryBrandVoiceWithAssets(id);
        dispatch(PRIVATE_ACTIONS.mergeBrandVoiceAssetsInCache(brandVoiceWithAssets));
      } catch (error) {
        captureMessage('Error while getting brand voice assets', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'BrandVoicesStore -> queryBrandVoiceWithAssets',
          },
        });
      }
    },

  getVoicesWithTerms:
    ({ language }) =>
    ({ dispatch }) => {
      const brandVoices = dispatch(ACTIONS.getBrandVoicesAssets({ language }));
      return brandVoices.filter((voice) => voice.brandTerms?.length > 0);
    },

  getDefaultVoiceCustomFormats:
    ({ language }) =>
    ({ dispatch }) => {
      const defaultVoiceAssets = dispatch(ACTIONS.getDefaultVoiceAssets({ language }));
      return defaultVoiceAssets?.customFormats || [];
    },

  setFormatGroupDefaults:
    ({ brandVoiceId, formatGroupId, on }) =>
    async ({ dispatch }) => {
      const updatedGroup = await setCustomFormatGroupDefaults({ brandVoiceId, formatGroupId, on });

      dispatch(
        PRIVATE_ACTIONS.updateFormatGroupActivity({
          brandVoiceId,
          formatGroupId,
          on: updatedGroup.on,
        })
      );
    },
};

export const BrandVoicesStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'brand-voices-store',
});

const prepareBrandVoicesAssets = (brandVoicesAssets) => {
  return brandVoicesAssets.map((bv) => ({
    ...bv,
    targetAudiences: markTargetAudiences(bv.targetAudiences || []),
  }));
};

// TODO: instead of marking BE-provided as non-suggested, we should somehow mark the "suggested" ones
const markTargetAudiences = (targetAudiences) => {
  return targetAudiences.map((ta) => ({ ...ta, isSuggested: false }));
};
